import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useCall } from '../../context/CallContext';
import { useChatPermissions } from '../../hooks/useChatPermissions';
import { db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import {
  subscribeToMessages,
  subscribeToTyping,
  sendMessage,
  setTypingStatus,
  markMessagesAsRead,
  sendFileMessage,
  cleanupOldMessages
} from '../../services/chatService';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import TypingIndicator from './TypingIndicator';

/**
 * ChatPanel - Main chat area with messages and input
 * Apple Messages-inspired design with light/dark mode support
 */

export default function ChatPanel({ chat, onClose, onInfoClick }) {
  const { user, userProfile } = useAuth();
  const { deleteChatConversation, leaveChatGroup, archiveChatConversation } = useChat();
  const { startCall, callStatus } = useCall();
  const { canShareFiles, canShareFile, maxFileSize, userId, userName } = useChatPermissions();

  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMeetingEnded, setIsMeetingEnded] = useState(false);
  const [otherUserName, setOtherUserName] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [fileError, setFileError] = useState(null);

  // Subscribe to messages
  useEffect(() => {
    if (!chat?.id) return;

    setLoading(true);
    const unsubscribe = subscribeToMessages(chat.id, (msgs) => {
      setMessages(msgs);
      setLoading(false);

      // Mark messages as read
      const unreadMessageIds = msgs
        .filter(m => m.senderId !== userId && !m.readBy?.[userId])
        .map(m => m.id);

      if (unreadMessageIds.length > 0 && userId) {
        markMessagesAsRead(chat.id, userId, unreadMessageIds).catch(console.error);
      }
    });

    // Cleanup old messages for free tier users
    if (userProfile?.subscriptionTier === 'free') {
      cleanupOldMessages(chat.id, 7).catch(console.error);
    }

    return () => unsubscribe();
  }, [chat?.id, userId, userProfile?.subscriptionTier]);

  // Subscribe to typing indicators
  useEffect(() => {
    if (!chat?.id || !userId) return;

    const unsubscribe = subscribeToTyping(chat.id, userId, setTypingUsers);
    return () => unsubscribe();
  }, [chat?.id, userId]);

  // Fetch other user's display name for DM chats
  useEffect(() => {
    if (chat?.type !== 'dm' || !chat?.participants) {
      setOtherUserName(null);
      return;
    }

    const fetchOtherUserName = async () => {
      try {
        const otherUserId = chat.participants.find(id => id !== userId);
        if (!otherUserId) return;

        const userRef = doc(db, 'users', otherUserId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          setOtherUserName(userData.displayName || userData.email || 'User');
        }
      } catch (error) {
        console.error('Error fetching other user name:', error);
      }
    };

    fetchOtherUserName();
  }, [chat?.type, chat?.participants, userId]);

  // Check if meeting chat's meeting has ended
  useEffect(() => {
    if (chat?.type !== 'meeting' || !chat?.meetingId || isMeetingEnded) {
      return;
    }

    const checkMeetingStatus = async () => {
      try {
        const meetingRef = doc(db, 'meetings', chat.meetingId);
        const meetingSnap = await getDoc(meetingRef);

        if (meetingSnap.exists()) {
          const meetingData = meetingSnap.data();
          const isEnded = meetingData.status === 'ended' ||
                         (meetingData.endedAt && new Date() > meetingData.endedAt.toDate());
          setIsMeetingEnded(isEnded);
        }
      } catch (error) {
        console.error('Error checking meeting status:', error);
      }
    };

    checkMeetingStatus();
    const interval = setInterval(checkMeetingStatus, 30000);
    return () => clearInterval(interval);
  }, [chat?.type, chat?.meetingId, isMeetingEnded]);

  // Handle sending message
  const handleSend = useCallback(async (text) => {
    if (!chat?.id || !userId) return;

    try {
      await sendMessage(
        chat.id,
        userId,
        userName,
        text,
        userProfile?.photoURL || null,
        false
      );
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [chat?.id, userId, userName, userProfile?.photoURL]);

  // Handle typing indicator
  const handleTyping = useCallback(async (isTyping) => {
    if (!chat?.id || !userId) return;

    try {
      await setTypingStatus(chat.id, userId, userName, isTyping);
    } catch (error) {
      // Ignore typing errors
    }
  }, [chat?.id, userId, userName]);

  // Handle file sharing
  const handleFileSelect = useCallback(async (file) => {
    if (!chat?.id || !userId || !file) return;

    setFileError(null);

    try {
      const fileSizeMB = file.size / (1024 * 1024);
      const validation = canShareFile(fileSizeMB, file.type);

      if (!validation.allowed) {
        setFileError(validation.reason);
        setTimeout(() => setFileError(null), 5000);
        return;
      }

      const storageRef = ref(storage, `chat-files/${chat.id}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      await sendFileMessage(
        chat.id,
        userId,
        userName,
        {
          name: file.name,
          size: file.size,
          type: file.type,
          url: downloadUrl
        },
        userProfile?.photoURL || null
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError('Failed to upload file. Please try again.');
      setTimeout(() => setFileError(null), 5000);
    }
  }, [chat?.id, userId, userName, userProfile, canShareFile]);

  // Handle delete/leave chat
  const handleDeleteChat = useCallback(async () => {
    if (!chat?.id) return;

    setProcessing(true);
    try {
      if (chat.type === 'group') {
        await leaveChatGroup(chat.id);
      } else {
        await deleteChatConversation(chat.id);
      }
      setShowDeleteConfirm(false);
      setShowMenu(false);
    } catch (error) {
      console.error('Error deleting/leaving chat:', error);
    } finally {
      setProcessing(false);
    }
  }, [chat?.id, chat?.type, deleteChatConversation, leaveChatGroup]);

  // Handle archive chat
  const handleArchiveChat = useCallback(async () => {
    if (!chat?.id) return;

    setShowMenu(false);
    try {
      await archiveChatConversation(chat.id);
    } catch (error) {
      console.error('Error archiving chat:', error);
    }
  }, [chat?.id, archiveChatConversation]);

  // Get chat type label
  const getContextLabel = () => {
    if (!chat) return '';
    switch (chat.type) {
      case 'meeting': return 'Meeting Chat';
      case 'group': return 'Group';
      case 'org_channel': return 'Organization';
      default: return '';
    }
  };

  // Get chat display name
  const getChatName = () => {
    if (!chat) return '';
    if (chat.type === 'org_channel') {
      return `#${chat.channelSlug || chat.name}`;
    }
    if (chat.type === 'dm' && chat.participants) {
      return otherUserName || 'Direct Message';
    }
    return chat.name || 'Chat';
  };

  if (!chat) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#f5f5f7] dark:bg-[#1c1c1e] px-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-white dark:bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
            <svg className="w-10 h-10 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-[#1d1d1f] dark:text-white/70 font-medium text-base mb-2">Select a conversation</h3>
          <p className="text-[#86868b] text-sm leading-relaxed">
            Choose a conversation from the list or start a new message
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#f5f5f7] dark:bg-[#1c1c1e] min-w-0">
      {/* Header */}
      <div className="bg-white/80 dark:bg-[#2c2c2e]/80 backdrop-blur-xl border-b border-[#d2d2d7] dark:border-white/10 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {/* Back button (mobile) */}
          <button
            onClick={onClose}
            className="lg:hidden w-9 h-9 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Chat icon */}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
            chat.type === 'org_channel'
              ? 'bg-gradient-to-br from-purple-500 to-blue-500'
              : chat.type === 'group'
              ? 'bg-gradient-to-br from-green-500 to-emerald-500'
              : chat.type === 'meeting'
              ? 'bg-gradient-to-br from-orange-500 to-red-500'
              : 'bg-gradient-to-br from-[#06c] to-[#0055d4]'
          }`}>
            {chat.type === 'org_channel' ? (
              <span className="text-white font-semibold text-sm">#</span>
            ) : chat.type === 'group' ? (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            ) : chat.type === 'meeting' ? (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
          </div>

          {/* Chat info */}
          <div className="min-w-0">
            <h2 className="text-[#1d1d1f] dark:text-white font-semibold truncate">{getChatName()}</h2>
            {getContextLabel() && (
              <p className="text-[#86868b] text-xs">{getContextLabel()}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Call buttons - only for DM chats */}
          {chat.type === 'dm' && chat.participants && (
            <>
              <button
                onClick={() => {
                  const otherUserId = chat.participants.find(id => id !== userId);
                  if (otherUserId) {
                    startCall(otherUserId, otherUserName, null, 'audio', chat.id);
                  }
                }}
                disabled={callStatus !== 'idle'}
                className="w-9 h-9 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-green-500/20 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Audio call"
              >
                <svg className="w-4 h-4 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </button>

              <button
                onClick={() => {
                  const otherUserId = chat.participants.find(id => id !== userId);
                  if (otherUserId) {
                    startCall(otherUserId, otherUserName, null, 'video', chat.id);
                  }
                }}
                disabled={callStatus !== 'idle'}
                className="w-9 h-9 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-green-500/20 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Video call"
              >
                <svg className="w-4 h-4 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </>
          )}

          {/* Menu button */}
          {chat.type !== 'org_channel' && chat.type !== 'meeting' && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="w-9 h-9 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-colors"
                title="More options"
              >
                <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-[#2c2c2e] rounded-xl border border-[#d2d2d7] dark:border-white/10 shadow-xl z-50 overflow-hidden">
                    <button
                      onClick={handleArchiveChat}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-[#1d1d1f] dark:text-white/80 hover:bg-[#f5f5f7] dark:hover:bg-white/10 transition-colors"
                    >
                      <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      <span className="text-sm">Archive</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowDeleteConfirm(true);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className="text-sm">{chat.type === 'group' ? 'Leave' : 'Delete'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Close button (desktop) */}
          <button
            onClick={onClose}
            className="hidden lg:flex w-9 h-9 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 items-center justify-center transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl border border-[#d2d2d7] dark:border-white/10 max-w-sm w-full p-6 shadow-xl">
            <h3 className="text-[#1d1d1f] dark:text-white font-semibold text-lg mb-2">
              {chat.type === 'group' ? 'Leave Group?' : 'Delete Conversation?'}
            </h3>
            <p className="text-[#86868b] text-sm mb-6">
              {chat.type === 'group'
                ? 'You will no longer receive messages from this group.'
                : 'This will permanently delete this conversation for both you and the other person.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 text-[#1d1d1f] dark:text-white font-medium rounded-xl transition-colors"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteChat}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
                disabled={processing}
              >
                {processing ? 'Processing...' : chat.type === 'group' ? 'Leave' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <MessageList
        messages={messages}
        currentUserId={userId}
        loading={loading}
      />

      {/* Typing indicator */}
      <TypingIndicator typingUsers={typingUsers} />

      {/* Meeting ended notice */}
      {isMeetingEnded && (
        <div className="bg-white/80 dark:bg-[#2c2c2e]/80 backdrop-blur-xl border-t border-[#d2d2d7] dark:border-white/10 px-4 py-3">
          <p className="text-[#86868b] text-xs text-center">
            This meeting has ended - conversation is now read-only
          </p>
        </div>
      )}

      {/* File upload error */}
      {fileError && (
        <div className="bg-red-500/10 border-t border-red-500/20 px-4 py-2 flex items-center justify-between">
          <p className="text-red-500 text-xs">{fileError}</p>
          <button
            onClick={() => setFileError(null)}
            className="text-red-500 hover:text-red-600 p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Input */}
      <MessageInput
        onSend={handleSend}
        onTyping={handleTyping}
        canShareFiles={canShareFiles && !isMeetingEnded}
        onFileSelect={handleFileSelect}
        placeholder={isMeetingEnded ? 'Meeting ended - read only' : `Message ${getChatName()}`}
        disabled={isMeetingEnded}
      />
    </div>
  );
}
